import React from "react";
import { useLang } from "@/i18n/LanguageContext";
import {
  Target,
  Workflow,
  Database,
  Cpu,
  ShieldCheck,
  Radar,
} from "lucide-react";
import Reveal from "./Reveal";

const PILLAR_ICONS = [Target, Workflow, Database, Cpu, ShieldCheck, Radar];

export default function Architecture() {
  const { t } = useLang();

  return (
    <section
      id="architecture"
      data-testid="architecture-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="sap-container">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
          <Reveal className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="sap-divider-gold" />
              <span className="sap-overline">{t.architecture.overline}</span>
            </div>
            <h2
              className="font-serif leading-[1.05] tracking-tight"
              style={{
                fontSize: "clamp(2.2rem, 4.4vw, 4rem)",
                color: "var(--sap-navy)",
                fontWeight: 400,
              }}
            >
              {t.architecture.title}
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={120}>
            <p className="text-base md:text-lg leading-relaxed text-neutral-600">
              {t.architecture.lead}
            </p>
          </Reveal>
        </div>

        {/* Signature image */}
        <Reveal>
          <div className="relative overflow-hidden group">
            <div
              className="absolute -top-4 -left-4 w-24 h-24 border-l border-t z-10"
              style={{ borderColor: "var(--sap-gold)" }}
            />
            <div
              className="absolute -bottom-4 -right-4 w-24 h-24 border-r border-b z-10"
              style={{ borderColor: "var(--sap-gold)" }}
            />
            <img
              src="/arquitetura-gestao.png"
              alt="Arquitetura da Gestão — Sapientia Consultores Associados"
              className="w-full h-auto object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.02]"
              loading="lazy"
            />
            <div
              className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
              style={{
                background:
                  "linear-gradient(to top, rgba(11,37,69,0.6), transparent)",
              }}
            />
          </div>
        </Reveal>

        {/* Cycle diagram removed — replaced by the dedicated "MetodoSapientia" section below */}

        {/* 6 Pillars */}
        <div className="mt-24">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
            <Reveal className="lg:col-span-8">
              <div className="flex items-center gap-3 mb-5">
                <span className="sap-divider-gold" />
                <span className="sap-overline">
                  {t.architecture.pillars_overline}
                </span>
              </div>
              <h3
                className="font-serif leading-[1.1] tracking-tight"
                style={{
                  fontSize: "clamp(1.7rem, 3.2vw, 2.6rem)",
                  color: "var(--sap-navy)",
                  fontWeight: 400,
                }}
              >
                {t.architecture.pillars_title}
              </h3>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
            {t.architecture.pillars.map((p, i) => {
              const Icon = PILLAR_ICONS[i] || Target;
              return (
                <Reveal key={i} delay={i * 60}>
                  <article
                    data-testid={`pillar-${i}`}
                    className="group relative bg-white p-8 md:p-10 h-full transition-colors duration-300 hover:bg-[color:var(--sap-navy)]"
                  >
                    <div className="flex items-start justify-between mb-6">
                      <div
                        className="inline-flex items-center justify-center w-12 h-12 border transition-colors"
                        style={{ borderColor: "#e7e7e7" }}
                      >
                        <Icon
                          size={20}
                          strokeWidth={1.5}
                          className="text-[color:var(--sap-navy)] group-hover:text-[color:var(--sap-gold)] transition-colors"
                        />
                      </div>
                      <span
                        className="font-serif text-2xl transition-colors"
                        style={{ color: "var(--sap-gold)" }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h4
                      className="font-serif text-xl md:text-2xl leading-snug mb-3 transition-colors"
                      style={{ color: "var(--sap-navy)", fontWeight: 500 }}
                    >
                      <span className="text-[color:var(--sap-navy)] group-hover:text-white">
                        {p.title}
                      </span>
                    </h4>
                    <p className="text-[14px] leading-relaxed text-neutral-600 group-hover:text-white/80 transition-colors">
                      {p.desc}
                    </p>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
